// The scorecard addendum's 15-question self-assessment. 1-5 scale unless
// noted otherwise; no branching — every visitor sees every question, in
// order. Answering all of them is the lead-gen gate (see RiskReview.tsx) —
// there is no separate email gate.

export type IncidentCountBucket = '0' | '1' | '2-3' | '4-7' | '8-15' | '16+';
export type CostBucket = 'under_5k' | '5k_25k' | '25k_100k' | '100k_500k' | 'over_500k';
export type ResolutionTimeBucket = 'same_day' | 'few_days' | '1_4_weeks' | 'over_month';
export type TrendDirection = 'down' | 'same' | 'up';

export interface SelfAssessmentAnswers {
  a1?: number;
  a2?: number;
  a3?: number;
  b1?: number;
  b2?: number;
  b3?: number;
  c1?: number;
  c2?: number;
  c3?: number;
  d1?: IncidentCountBucket;
  d2?: CostBucket;
  d3?: ResolutionTimeBucket;
  r1?: number;
  r2?: TrendDirection;
  r3?: number;
}

const REQUIRED_KEYS: (keyof SelfAssessmentAnswers)[] = [
  'a1', 'a2', 'a3', 'b1', 'b2', 'b3', 'c1', 'c2', 'c3', 'd1', 'd2', 'd3', 'r1', 'r2', 'r3',
];

export function isAssessmentComplete(a: SelfAssessmentAnswers): boolean {
  return REQUIRED_KEYS.every((key) => a[key] !== undefined);
}

export function answeredCount(a: SelfAssessmentAnswers): number {
  return REQUIRED_KEYS.filter((key) => a[key] !== undefined).length;
}

export const TOTAL_QUESTIONS = REQUIRED_KEYS.length;

export interface ScaleQuestion {
  id: 'a1' | 'a2' | 'a3' | 'b1' | 'b2' | 'b3' | 'c1' | 'c2' | 'c3' | 'r1' | 'r3';
  number: string;
  text: string;
  loLabel: string;
  hiLabel: string;
}

export interface QuestionGroup {
  key: 'A' | 'B' | 'C' | 'D' | 'R';
  title: string;
  scaleQuestions: ScaleQuestion[];
}

export const GROUPS: QuestionGroup[] = [
  {
    key: 'A',
    title: 'Detection & Avoidance Readiness',
    scaleQuestions: [
      {
        id: 'a1',
        number: 'A.1',
        text: "How confident are you that each person on your team — not just the most security-aware ones — could correctly identify a common scam attempt (fake invoice, executive impersonation, urgent wire request, etc.) before acting on it?",
        loLabel: 'Not confident at all',
        hiLabel: 'Very confident',
      },
      {
        id: 'a2',
        number: 'A.2',
        text: 'How much training has your team received on recognizing social engineering or scam attempts in the last 12 months?',
        loLabel: 'None',
        hiLabel: 'Regular, ongoing training',
      },
      {
        id: 'a3',
        number: 'A.3',
        text: "If an employee received a request that appeared to come from a manager or executive, how likely is it they'd verify it through a second channel (call, in-person, separate app) before acting on it?",
        loLabel: 'Very unlikely to check',
        hiLabel: 'Would always verify first',
      },
    ],
  },
  {
    key: 'B',
    title: 'Incident Response / Clean-up Readiness',
    scaleQuestions: [
      {
        id: 'b1',
        number: 'B.1',
        text: 'If an employee fell for a scam today, how clear is your team on exactly what to do in the first hour (who to notify, what to freeze or reverse)?',
        loLabel: 'No idea',
        hiLabel: 'Documented plan everyone knows',
      },
      {
        id: 'b2',
        number: 'B.2',
        text: 'How well established is your process for employees to report a suspected scam, even one they only realize was suspicious after the fact?',
        loLabel: 'No real process',
        hiLabel: 'Clear, well-known process everyone would use',
      },
      {
        id: 'b3',
        number: 'B.3',
        text: 'If money were wrongly sent as a result of a scam, how confident are you that your organization could move fast enough (bank contact, fraud reporting) to have a real chance at recovery?',
        loLabel: 'Not confident at all',
        hiLabel: 'Very confident',
      },
    ],
  },
  {
    key: 'C',
    title: 'Preventative Measures In Place',
    scaleQuestions: [
      {
        id: 'c1',
        number: 'C.1',
        text: 'How much does your business actually require employee names and contact details to be public (e.g., agent directories, public staff bios, "meet the team" pages) versus that being optional?',
        loLabel: 'Not required at all - could easily be private',
        hiLabel: 'Fully required for the business to function',
      },
      {
        id: 'c2',
        number: 'C.2',
        text: 'How many extra checks or safeguards do you have in place today to catch things like fake invoices or urgent money requests before they go through (e.g., a second person has to sign off, suspicious emails get flagged automatically, calls get verified)?',
        loLabel: 'None in place',
        hiLabel: 'Several checks in place',
      },
      {
        id: 'c3',
        number: 'C.3',
        text: 'When someone receives an unusual or urgent request for money or sensitive information, how often does your team double-check it another way before acting on it?',
        loLabel: 'Rarely or never',
        hiLabel: 'Every time',
      },
    ],
  },
  {
    key: 'R',
    title: 'Risk Perception',
    scaleQuestions: [
      {
        id: 'r1',
        number: 'R.1',
        text: 'How worried are you, today, about your organization being targeted by a scam?',
        loLabel: 'Not worried at all',
        hiLabel: 'Very worried',
      },
      {
        id: 'r3',
        number: 'R.3',
        text: 'How concerned are you about AI being used to make scams targeting your organization more convincing or harder to detect?',
        loLabel: 'Not concerned at all',
        hiLabel: 'Very concerned',
      },
    ],
  },
];

export const D1_OPTIONS: { value: IncidentCountBucket; label: string }[] = [
  { value: '0', label: '0' },
  { value: '1', label: '1' },
  { value: '2-3', label: '2-3' },
  { value: '4-7', label: '4-7' },
  { value: '8-15', label: '8-15' },
  { value: '16+', label: '16+' },
];

export const D2_OPTIONS: { value: CostBucket; label: string }[] = [
  { value: 'under_5k', label: 'Under $5K' },
  { value: '5k_25k', label: '$5K-$25K' },
  { value: '25k_100k', label: '$25K-$100K' },
  { value: '100k_500k', label: '$100K-$500K' },
  { value: 'over_500k', label: 'Over $500K' },
];

export const D3_OPTIONS: { value: ResolutionTimeBucket; label: string }[] = [
  { value: 'same_day', label: 'Same day' },
  { value: 'few_days', label: 'A few days' },
  { value: '1_4_weeks', label: '1-4 weeks' },
  { value: 'over_month', label: 'Over a month' },
];

export const R2_OPTIONS: { value: TrendDirection; label: string }[] = [
  { value: 'down', label: 'Gone down' },
  { value: 'same', label: 'Stayed the same' },
  { value: 'up', label: 'Gone up' },
];

export const D1_QUESTION = 'How many social engineering incidents, attempted or successful, has your organization experienced in the last 12 months?';
export const D2_QUESTION = 'If your organization experienced a successful scam, what direct financial impact (money or assets actually lost, not counting clean-up time) would you estimate it could cause per incident?';
export const D3_QUESTION = 'If a scam succeeded today, how long do you think it would take your organization to fully detect and resolve it?';
export const R2_QUESTION = "Compared to a year ago, do you think your organization's risk of being targeted has gone up, stayed the same, or gone down?";
